import { ChangeDetectionStrategy, Component, DestroyRef, TemplateRef, ViewContainerRef, effect, inject, input, output, viewChild } from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { Overlay, type OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { SidebarComponent } from '../sidebar/sidebar.component';

// Reemplaza el drawer mobile manual de _nav.js (creaba `overlay`/`drawer` con
// `document.createElement` + `style.transform`/`style.display` a mano). Usa
// @angular/cdk/overlay para el backdrop + cierre por click-fuera/Escape, y
// @angular/cdk/a11y (`cdkTrapFocus`) para que el foco no se escape del drawer mientras está
// abierto — ninguno de los dos existía en la versión HTML plana.
@Component({
  selector: 'app-mobile-drawer',
  standalone: true,
  imports: [A11yModule, SidebarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './mobile-drawer.component.html',
})
export class MobileDrawerComponent {
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);

  private readonly drawerTemplate = viewChild.required<TemplateRef<unknown>>('drawerTemplate');

  readonly open = input(false);
  readonly closed = output<void>();

  private overlayRef: OverlayRef | null = null;

  constructor() {
    effect(() => {
      if (this.open()) {
        this.openOverlay();
      } else {
        this.disposeOverlay();
      }
    });

    inject(DestroyRef).onDestroy(() => this.disposeOverlay());
  }

  requestClose(): void {
    this.closed.emit();
  }

  private openOverlay(): void {
    if (this.overlayRef) {
      return;
    }

    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
      positionStrategy: this.overlay.position().global().left('0').top('0'),
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });

    this.overlayRef.attach(new TemplatePortal(this.drawerTemplate(), this.viewContainerRef));
    this.overlayRef.backdropClick().subscribe(() => this.requestClose());
    this.overlayRef.keydownEvents().subscribe((event) => {
      if (event.key === 'Escape') {
        this.requestClose();
      }
    });
  }

  private disposeOverlay(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }
}
